import { CalendarId, RepeatKind, type ImportantDateValue } from '@wiztime/core';
import type { TFunction } from 'i18next';
import type { CalendarApi } from './calendarModule';
import { Countdown, countdownOf } from './model';

/** Texts of the important dates UI that depend on the calendar module and the language. */

const pad = (value: number, width: number): string => String(value).padStart(width, '0');

/** Name of a lunar month, "Leap" prefixed for a leap month. */
export function lunarMonthName(t: TFunction, month: number, leap: boolean): string {
  const name = t(`calendar.lunar.month.${month}`, { defaultValue: String(month) });
  return leap ? t('calendar.lunar.leapMonth', { name }) : name;
}

export function lunarDayName(t: TFunction, day: number): string {
  return t(`calendar.lunar.day.${day}`, { defaultValue: String(day) });
}

/**
 * A date in its own calendar. Gregorian: `2026-11-02`. Lunar: with the year when it does not
 * repeat, month and day when it repeats yearly, the day alone when it repeats monthly.
 */
export function dateLabel(t: TFunction, date: ImportantDateValue, repeat: RepeatKind): string {
  if (date.calendar === CalendarId.Gregorian) {
    return `${pad(date.year, 4)}-${pad(date.month, 2)}-${pad(date.day, 2)}`;
  }
  const day = lunarDayName(t, date.day);
  if (repeat === RepeatKind.Monthly) return t('importantDates.lunarDay', { day });
  const month = lunarMonthName(t, date.month, date.leap);
  return repeat === RepeatKind.Yearly
    ? t('importantDates.lunarMonthDay', { month, day })
    : t('importantDates.lunarFull', { year: date.year, month, day });
}

/** The lunar date of a Gregorian date, or null outside the calendar's range. */
export function lunarLabelOf(calendar: CalendarApi, t: TFunction, gregorian: string): string | null {
  try {
    const lunar = calendar.fromGregorian(gregorian, CalendarId.ChineseLunar);
    return t('importantDates.lunarMonthDay', {
      month: lunarMonthName(t, lunar.month, lunar.leap),
      day: lunarDayName(t, lunar.day),
    });
  } catch {
    return null;
  }
}

/** "Today", "Tomorrow", "In 5 days". */
export function countdownLabel(t: TFunction, daysUntil: number): string {
  switch (countdownOf(daysUntil)) {
    case Countdown.Today:
      return t('importantDates.today');
    case Countdown.Tomorrow:
      return t('importantDates.tomorrow');
    case Countdown.Days:
      return t('importantDates.inDays', { count: daysUntil });
  }
}
